import { Linktrainrelaymixer } from '@linktivity/eticketportal';

/* -------------------------------------------------------------------------- */
/* SDK types (re-exported so the UI depends on the generated SDK, cf.          */
/* china-railway/services/types.ts which aliases Piaoduoduomixer).             */
/* -------------------------------------------------------------------------- */

/** A selectable station, `{ code, name }`. */
type Station = Linktrainrelaymixer.Station;
/** Station-list response — flat `depStations` / `arrStations`. */
type GetStationListResp = Linktrainrelaymixer.GetStationListResp;
/** Supplier station response — carries the orderable date / time ranges. */
type GetStationsResp = Linktrainrelaymixer.GetStationsResp;
/** A backend-provided orderable departure-time window `{ from, to }`. */
type OrderableTimeRange = Linktrainrelaymixer.OrderableTimeRange;
type CommonReq = Linktrainrelaymixer.CommonReq;
type SearchDiagramsReq = Linktrainrelaymixer.SearchDiagramsReq;
type SearchDiagramsResp = Linktrainrelaymixer.SearchDiagramsResp;
/** A single train (schedule) row in the train list. */
type Diagram = Linktrainrelaymixer.Diagram;
/** A car (车厢) reference on a diagram — `{ carId, name }`. */
type DiagramCarInfo = Linktrainrelaymixer.DiagramCarInfo;
/** A seat class (车厢等级) with its own remain-status and price. */
type SeatClass = Linktrainrelaymixer.SeatClass;
type Price = Linktrainrelaymixer.Price;
type Locale = Linktrainrelaymixer.Locale;

/** Seat-availability status: UNKNOWN / AVAILABLE / LIMITED / SOLD_OUT. */
type RemainStatus = Linktrainrelaymixer.RemainStatus;
const RemainStatus = Linktrainrelaymixer.RemainStatus;

/* --- Order flow (0.1.29) --------------------------------------------------- */

/** Order-parameters (预约参数) request / response — drives the confirm screen. */
type GetOrderParametersReq = Linktrainrelaymixer.GetOrderParametersReq;
type GetOrderParametersResp = Linktrainrelaymixer.GetOrderParametersResp;
/** A backend-provided seat option `{ code, name, paymentPrice, displayPrice }`. */
type SeatPreference = Linktrainrelaymixer.SeatPreference;
/** A priced line item on an order (票价明细). */
type OrderPriceUnit = Linktrainrelaymixer.OrderPriceUnit;
type LinktrainrelaymixerUnit = Linktrainrelaymixer.LinktrainrelaymixerUnit;
/** An optional add-on product line (增值服务). */
type AddOnItem = Linktrainrelaymixer.AddOnItem;

/** Price-preview request / response before committing an order. */
type CalculateOrderPriceReq = Linktrainrelaymixer.CalculateOrderPriceReq;
type CalculateOrderPriceResp = Linktrainrelaymixer.CalculateOrderPriceResp;

/** Create / cancel order request / response. */
type CreateOrderReq = Linktrainrelaymixer.CreateOrderReq;
type CreateOrderResp = Linktrainrelaymixer.CreateOrderResp;
type CancelOrderResp = Linktrainrelaymixer.CancelOrderResp;

/** Order detail / list responses. */
type GetOrderDetailsResp = Linktrainrelaymixer.GetOrderDetailsResp;
type GetOrderListResp = Linktrainrelaymixer.GetOrderListResp;
/** Backend order lifecycle status (payment → confirmed → completed). */
type OrderStatus = Linktrainrelaymixer.OrderStatus;
const OrderStatus = Linktrainrelaymixer.OrderStatus;
/** External ticketing (出票) status on an order detail. */
type ExternalTicketingStatus = Linktrainrelaymixer.ExternalTicketingStatus;
const ExternalTicketingStatus = Linktrainrelaymixer.ExternalTicketingStatus;
type SeatPreferenceCode = NonNullable<CreateOrderReq['seatPreferenceCode']>;

/* --- Seat map (0.1.29) ----------------------------------------------------- */

type GetSeatMapReq = Linktrainrelaymixer.GetSeatMapReq;
type GetSeatMapResp = Linktrainrelaymixer.GetSeatMapResp;
/** A single seat cell on the seat-map grid. */
type SeatMapSeat = Linktrainrelaymixer.SeatMapSeat;

/* --- Banners (0.1.29) ------------------------------------------------------ */

type GetBannerListResp = Linktrainrelaymixer.GetBannerListResp;

/* -------------------------------------------------------------------------- */
/* View state not modelled by structured API fields                           */
/* -------------------------------------------------------------------------- */

/** A quick departure-time filter bucket (UI-only convenience filter). */
interface TimeRange {
  from: string;
  to: string;
}

type BookingInfo = GetOrderDetailsResp & { bookingId?: string };

/** The user's in-flight selection carried across the booking flow. */
interface BookingSelection {
  fromStation?: Station;
  toStation?: Station;
  /** Whether departure / arrival use the opposite station-option lists. */
  routeReversed?: boolean;
  diagram?: Diagram;
  seatClass?: SeatClass;
  seatPreference?: SeatPreference;
  seatPreferenceCode?: SeatPreferenceCode;
}

interface DateOption {
  value: string;
  label: string;
  weekday: string;
}

export type {
  Station,
  GetStationListResp,
  GetStationsResp,
  OrderableTimeRange,
  CommonReq,
  Locale,
  SearchDiagramsReq,
  SearchDiagramsResp,
  Diagram,
  DiagramCarInfo,
  SeatClass,
  Price,
  SeatPreference,
  OrderPriceUnit,
  LinktrainrelaymixerUnit,
  AddOnItem,
  SeatPreferenceCode,
  GetOrderParametersReq,
  GetOrderParametersResp,
  CalculateOrderPriceReq,
  CalculateOrderPriceResp,
  CreateOrderReq,
  CreateOrderResp,
  CancelOrderResp,
  GetOrderDetailsResp,
  GetOrderListResp,
  GetSeatMapReq,
  GetSeatMapResp,
  SeatMapSeat,
  GetBannerListResp,
  TimeRange,
  BookingInfo,
  BookingSelection,
  DateOption
};

export { RemainStatus, OrderStatus, ExternalTicketingStatus };
